import ResponsiveImage from "./common/ResponsiveImage";
import { tr, usePartnerTranslation } from "../lib/language";
import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ChevronDown, Check, LogOut, Store, Plus, Images, Pencil, UserCog, KeyRound, ShieldCheck } from "lucide-react";
import axios from "axios";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { SUPPLIER_LOGO, LANGUAGES } from "../lib/constants";
import { useAuth, canAccess, isPrivileged } from "../lib/auth";
import { publicShopUrl } from "../lib/shopUrls";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PORTAL_NAV = [
  { to: "/home", get label() { return tr("Home"); }, labelKey: "nav.home", key: "home" },
  { to: "/create", get label() { return tr("Create"); }, labelKey: "nav.create", key: "create" },
  { to: "/products", get label() { return tr("Products"); }, labelKey: "nav.products", key: "products" },
  { to: "/bookings", get label() { return tr("Bookings"); }, labelKey: "nav.bookings", key: "bookings" },
  { to: "/finance", get label() { return tr("Finance"); }, labelKey: "nav.finance", key: "finance" },
  { to: "/refund-requests", get label() { return tr("Refund requests"); }, labelKey: "nav.refunds", key: "refunds" },
  { to: "/support", get label() { return tr("Support"); }, labelKey: "nav.support", key: "support" },
];

const makeShopItems = (handle, t = (key, fallback) => fallback || key) => [
  {
    to: publicShopUrl(handle),
    label: t("shop.visit", "Visit shop"),
    icon: Store,
    external: true,
  },
  { to: "/create", label: t("shop.createProduct", "Create a product"), icon: Plus, restrictedTo: ["administration", "operations"] },
  { to: "/my-shop/gallery", label: t("shop.uploadGallery", "Upload in gallery"), icon: Images, restrictedTo: ["administration", "operations"] },
  { to: "/my-shop/edit", label: t("shop.editDetails", "Edit shop details"), icon: Pencil, restrictedTo: ["administration", "operations"] },
];

const LanguageDropdown = ({ language, setLanguage }) => {
  usePartnerTranslation();
  const languageItems = LANGUAGES.flatMap((group) => group.items);
  const currentLabel =
    languageItems.find((l) => l.code === language)?.label ||
    "English";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid="language-dropdown-trigger"
        className="group inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-medium text-[#0F1115] outline-none transition-colors hover:text-[#2476FF] focus-visible:ring-2 focus-visible:ring-[#2476FF]/40"
      >
        <span>{currentLabel}</span>
        <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={10}
        className="w-60 rounded-xl border border-[#EAECEF] p-1 shadow-lg"
        data-testid="language-dropdown-content"
      >
        {languageItems.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            data-testid={`language-item-${lang.code}`}
            onClick={() => setLanguage?.(lang.code)}
            className="flex items-center justify-between gap-3 rounded-md px-3 py-2 text-sm"
          >
            <div className="flex flex-col">
              <span className="font-medium text-[#0F1115]">
                {lang.label}
              </span>
              <span className="text-xs text-[#6B7280]">
                {lang.region}
              </span>
            </div>
            {language === lang.code && (
              <Check className="h-4 w-4 text-[#2476FF]" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const GuestRight = ({
  language,
  setLanguage
}) => {
  usePartnerTranslation();
  return <div className="flex items-center">
    <LanguageDropdown language={language} setLanguage={setLanguage} />
  </div>;
};

const portalLinkClass = ({ isActive }) =>
  [
    "relative text-sm font-medium transition-colors",
    isActive ? "text-[#0F1115]" : "text-[#5b5f66] hover:text-[#0F1115]",
  ].join(" ");

const PortalMiddle = ({ t }) => {
  usePartnerTranslation();
  const { user, role } = useAuth();
  const items = PORTAL_NAV.filter((item) => user?.is_owner || canAccess(role, item.key));
  return (
  <nav
    data-testid="portal-nav"
    className="hidden items-center gap-8 md:flex"
    aria-label={tr("Main")}
  >
    {items.map((item) => (
      <NavLink
        key={item.to}
        to={item.to}
        data-testid={`nav-${item.key}`}
        className={portalLinkClass}
      >
        {({ isActive }) => (
          <>
            <span>{t?.(item.labelKey, item.label) || item.label}</span>
            {isActive && (
              <span className="absolute -bottom-[22px] start-0 end-0 h-[2px] rounded-full bg-[#FF5757]" />
            )}
          </>
        )}
      </NavLink>
    ))}
    <MyShopDropdown t={t} />
  </nav>
  );
};

const MyShopDropdown = ({ t }) => {
  usePartnerTranslation();
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const [handle, setHandle] = useState("");

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/shops/me`)
      .then((r) => setHandle(r.data?.handle || ""))
      .catch(() => {});
  }, [user]);

  // Also stay in sync when the shop is updated elsewhere in the portal
  useEffect(() => {
    const onUpdated = (e) => { if (e.detail?.handle) setHandle(e.detail.handle); };
    window.addEventListener("humrahe:shop-updated", onUpdated);
    return () => window.removeEventListener("humrahe:shop-updated", onUpdated);
  }, []);

  const items = makeShopItems(handle, t).filter(
    (i) => !i.restrictedTo || user?.is_owner || i.restrictedTo.includes(role),
  );
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid="nav-my-shop-trigger"
        className="group inline-flex items-center gap-1 text-sm font-medium text-[#5b5f66] outline-none transition-colors hover:text-[#0F1115] focus-visible:text-[#0F1115]"
      >
        <span>{t?.("nav.myShop", tr("My Shop")) || tr("My Shop")}</span>
        <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={16}
        data-testid="my-shop-menu"
        className="w-60 rounded-xl border border-[#EAECEF] p-1 shadow-lg"
      >
        {items.map(({ to, label, icon: Icon, external }) => (
          <DropdownMenuItem
            key={to}
            data-testid={`my-shop-item-${to.split("/").pop()}`}
            onClick={() => {
              if (external) {
                window.open(to, "_blank", "noopener,noreferrer");
              } else {
                navigate(to);
              }
            }}
            className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium"
          >
            <Icon className="h-4 w-4 text-[#6B7280]" strokeWidth={1.8} />
            <span>{label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const UserMenu = ({ language, setLanguage, t }) => {
  usePartnerTranslation();
  const { user, role, logout } = useAuth();
  const navigate = useNavigate();
  const initial = (user?.email || "U").trim().charAt(0).toUpperCase();
  const [shopInfo, setShopInfo] = useState({ handle: null, profileImage: "", supplierCode: "" });
  const languageItems = LANGUAGES.flatMap((group) => group.items);
  const navItems = PORTAL_NAV.filter((item) => user?.is_owner || canAccess(role, item.key));
  const shopItems = makeShopItems(shopInfo.handle, t).filter(
    (i) => !i.restrictedTo || user?.is_owner || i.restrictedTo.includes(role),
  );

  useEffect(() => {
    let cancelled = false;
    if (!user) return;
    const loadShop = async () => {
      try {
        const r = await axios.get(`${API}/shops/me`);
        if (!cancelled) {
          setShopInfo({
            handle: r.data?.handle || null,
            profileImage: r.data?.profile_image || "",
            supplierCode: r.data?.supplier_code || "",
          });
        }
      } catch {
        if (!cancelled) setShopInfo({ handle: null, profileImage: "", supplierCode: "" });
      }
    };
    const onShopUpdated = (event) => {
      const shop = event.detail || {};
      setShopInfo((prev) => ({
        ...prev,
        handle: shop.handle || prev.handle,
        profileImage:
          shop.profile_image !== undefined
            ? shop.profile_image || ""
            : prev.profileImage,
      }));
    };

    loadShop();
    window.addEventListener("humrahe:shop-updated", onShopUpdated);
    return () => {
      cancelled = true;
      window.removeEventListener("humrahe:shop-updated", onShopUpdated);
    };
  }, [user]);

  const handleSignOut = () => {
    logout();
    navigate("/");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid="user-menu-trigger"
        className="inline-flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-[#FF5757] text-sm font-semibold text-white outline-none ring-1 ring-[#EFE4E4] transition-transform hover:scale-105 focus-visible:ring-2 focus-visible:ring-[#FF5757]/40"
        aria-label={tr("Account menu")}
      >
        {shopInfo.profileImage ? (
          <ResponsiveImage imageWidth={160} sizes="80px"
            src={shopInfo.profileImage}
            alt={tr("Company logo")}
            className="h-full w-full object-cover"
          />
        ) : (
          initial
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={10}
        data-testid="user-menu"
        className="w-64 rounded-xl border border-[#EAECEF] p-1 shadow-lg"
      >
        <div className="px-3 py-2">
          <p className="text-xs text-[#6B7280]">{t?.("account.signedInAs", tr("Signed in as")) || tr("Signed in as")}</p>
          <p
            className="mt-0.5 truncate text-sm font-semibold text-[#0F1115]"
            data-testid="user-menu-email"
          >
            {user?.email}
          </p>
          {shopInfo.handle && (
            <p
              data-testid="user-menu-supplier-id"
              aria-label={tr("Handle")}
              className="mt-1 truncate text-[11px] font-medium text-[#6B7280]"
            >{tr("Handle @")}{shopInfo.handle.replace(/^@+/, "")}
            </p>
          )}
          {shopInfo.supplierCode && (
            <p
              data-testid="user-menu-supplier-code"
              className="mt-0.5 truncate text-[11px] font-medium text-[#6B7280]"
            >{tr("Supplier code")}: <span className="font-semibold text-[#0F1115]">{shopInfo.supplierCode}</span>
            </p>
          )}
        </div>
        <DropdownMenuSeparator />
        <div className="md:hidden">
          {navItems.map((item) => (
            <DropdownMenuItem
              key={item.to}
              data-testid={`user-menu-mobile-${item.key}`}
              onClick={() => navigate(item.to)}
              className="flex cursor-pointer items-center rounded-md px-3 py-2 text-sm font-medium text-[#0F1115]"
            >
              {t?.(item.labelKey, item.label) || item.label}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <div className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]">
            {t?.("nav.myShop", tr("My Shop")) || tr("My Shop")}
          </div>
          {shopItems.map(({ to, label, icon: Icon, external }) => (
            <DropdownMenuItem
              key={to}
              data-testid={`user-menu-mobile-shop-${to.split("/").pop()}`}
              onClick={() => {
                if (external) {
                  window.open(to, "_blank", "noopener,noreferrer");
                } else {
                  navigate(to);
                }
              }}
              className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-[#0F1115]"
            >
              <Icon className="h-4 w-4 text-[#6B7280]" strokeWidth={1.8} />
              {label}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
        </div>
        <div className="md:hidden">
          <div className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]">
            {t?.("account.language", tr("Language")) || tr("Language")}
          </div>
          {languageItems.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              data-testid={`user-menu-language-${lang.code}`}
              onClick={() => setLanguage?.(lang.code)}
              className="flex cursor-pointer items-center justify-between gap-3 rounded-md px-3 py-2 text-sm font-medium text-[#0F1115]"
            >
              <span>{lang.label}</span>
              {language === lang.code && (
                <Check className="h-4 w-4 text-[#2476FF]" />
              )}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
        </div>
        {(user?.is_owner || isPrivileged(user?.role)) && (
          <DropdownMenuItem
            data-testid="user-menu-account"
            onClick={() => navigate("/account")}
            className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-[#0F1115]"
          >
            <UserCog className="h-4 w-4 text-[#6B7280]" strokeWidth={1.8} />
            {t?.("account.management", tr("Account management")) || tr("Account management")}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          data-testid="user-menu-change-password"
          onClick={() => navigate("/account/change-password")}
          className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-[#0F1115]"
        >
          <KeyRound className="h-4 w-4 text-[#6B7280]" strokeWidth={1.8} />
          {t?.("account.changePassword", tr("Change password")) || tr("Change password")}
        </DropdownMenuItem>
        {(user?.is_owner || isPrivileged(user?.role)) && (
          <DropdownMenuItem
            data-testid="user-menu-legal"
            onClick={() => navigate("/account/legal")}
            className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-[#0F1115]"
          >
            <ShieldCheck className="h-4 w-4 text-[#6B7280]" strokeWidth={1.8} />
            {t?.("account.legalCenter", tr("Legal center")) || tr("Legal center")}
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          data-testid="user-menu-signout"
          onClick={handleSignOut}
          className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-[#0F1115]"
        >
          <LogOut className="h-4 w-4" />
          {t?.("account.signOut", tr("Sign out")) || tr("Sign out")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const PortalRight = ({
  language,
  setLanguage,
  t
}) => {
  usePartnerTranslation();
  return <div className="flex items-center gap-3 md:gap-5">
    <div className="hidden md:block">
      <LanguageDropdown language={language} setLanguage={setLanguage} />
    </div>
    <UserMenu language={language} setLanguage={setLanguage} t={t} />
  </div>;
};

export const Header = ({ language, setLanguage, t }) => {
  usePartnerTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAuthed = !!user;

  return (
    <header
      data-testid="site-header"
      className="w-full border-b border-[#EAECEF] bg-white"
    >
      <div className="mx-auto flex h-[76px] max-w-[1400px] items-center justify-between px-6 md:px-10">
        <div className="flex items-center gap-6 md:gap-10">
          <button
            data-testid="header-logo-btn"
            onClick={() => navigate(isAuthed ? "/home" : "/")}
            className="flex items-center gap-2 transition-opacity hover:opacity-80"
            aria-label={tr("Voocly home")}
          >
            <ResponsiveImage imageWidth={160} sizes="80px"
              src={SUPPLIER_LOGO}
              alt="Voocly Partner"
              className="h-9 w-auto md:h-10"
            />
          </button>

          {isAuthed && <PortalMiddle t={t} />}
        </div>

        <div className="flex items-center gap-3">
          {isAuthed ? (
            <PortalRight language={language} setLanguage={setLanguage} t={t} />
          ) : (
            <GuestRight language={language} setLanguage={setLanguage} />
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
